import React, {Component} from 'react';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import {FormattedHTMLMessage, injectIntl} from 'react-intl';
import PropTypes from 'prop-types';
import ProjectRetainerPeriodHeader from './ProjectRetainerPeriodHeader';
import ProjectRetainerPeriodDataVisuals from './ProjectRetainerPeriodDataVisuals';
import ProjectRetainerPeriodTable from './ProjectRetainerPeriodTable';
import ProjectRetainerPeriodExpensesTable from './ProjectRetainerPeriodExpensesTable';
import Util from '../../../shared/util/util';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {BUTTON_COLOR, BUTTON_STYLE, PERIOD_BUDGET_TYPE} from '../../../../constants';
import UpdateRetainerPeriodMutation from '../../../../mutations/update_retainer_period_mutation';
import UpdateTimeRegistrationRetainerConflict from '../../../../mutations/update_time_registration_retainer_conflict_mutation';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {dispatch, EVENT_ID} from '../../../../containers/event_manager';
import {getPeriodExpensesTotal, getRevertableRolloversFromPeriods, getRolloversFromPeriods} from './RetainerPeriodUtil';
import {isProjectLocked} from '../../../shared/util/projectStageUtil';

function getRetainerColumns() {
	return [
		{
			checked: true,
			minWidth: 80,
			maxWidth: 80,
			name: 'task-id',
			translationId: 'project_board.card_details_selector_task_id',
			width: null,
			retainerDefault: true,
			independant: true,
		},
		{
			checked: true,
			minWidth: 200,
			name: 'task-name',
			translationId: 'common.task_project',
			width: null,
			align: 'left',
			hide: true,
			retainerDefault: true,
		},
		{
			checked: true,
			minWidth: 150,
			maxWidth: 150,
			name: 'invoiced',
			translationId: 'common.locked_entry',
			width: null,
			align: 'center',
			retainerDefault: true,
		},
		{
			checked: true,
			minWidth: 150,
			maxWidth: 150,
			name: 'date',
			translationId: 'common.date',
			width: null,
			align: 'center',
			retainerDefault: true,
		},
		{
			checked: false,
			minWidth: 166,
			maxWidth: 166,
			name: 'harvest',
			translationId: 'card_modal.harvest_task',
			width: null,
			retainerDefault: false,
		},
		{
			checked: true,
			minWidth: 90,
			maxWidth: 90,
			name: 'time-entries',
			translationId: 'common.time_entry',
			width: null,
			align: 'right',
			retainerDefault: true,
			paddingRight: 16,
		},
		{
			checked: true,
			minWidth: 110,
			maxWidth: 110,
			name: 'price',
			translationId: 'common.value_of_service',
			width: null,
			retainerDefault: true,
			align: 'right',
			paddingRight: 16,
		},
		{
			checked: true,
			minWidth: 166,
			maxWidth: 166,
			name: 'person',
			translationId: 'common.person',
			width: null,
			retainerDefault: true,
		},
		{
			checked: true,
			minWidth: 18,
			maxWidth: 18,
			name: 'chip-right',
			translationId: null,
			hide: true,
			width: null,
			retainerDefault: true,
			align: 'center',
		},
	];
}

class ProjectRetainerPeriod extends Component {
	constructor(props) {
		super(props);
		this.state = {
			expanded: this.props.startExpanded,
			availableColumns: this.getAvailableColumns(),
			taskCellMaxWidth: 200,
		};
		this.onWindowResize = this.onWindowResize.bind(this);
	}

	componentDidMount() {
		window.addEventListener('resize', this.onWindowResize);
		this.updateTaskCellMaxWidth();
	}

	componentDidUpdate(prevProps, prevState) {
		const width = this.getTaskCellMaxWidth();
		if (this.props.containerWidth !== prevProps.containerWidth || width !== this.state.taskCellMaxWidth) {
			this.updateTaskCellMaxWidth();
		}
	}

	getContainerWidth() {
		return (
			Math.max(document.documentElement.clientWidth, window.innerWidth) -
			32 * 2 - // margin on each side
			(this.props.sideNavLocked ? 248 : 0)
		);
	}

	getAvailableColumns() {
		const cols = getRetainerColumns();
		if (this.props.project.harvestProject) {
			cols.map(col => (col.name === 'harvest' ? (col.checked = true) : null));
		}
		return cols;
	}

	getTaskCellMaxWidth() {
		// Period header cannot grow smaller than 1020px
		const w = Math.max(this.getContainerWidth(), 1020);
		// Calculate space for other cols only

		// Space used by other headers than task name
		const usedSpace =
			this.state.availableColumns.reduce(
				(acc, col) => ((col.checked || col.maintainSpace) && col.name !== 'task-name' ? col.maxWidth + acc : acc),
				0
			) + 32; // 32 is 2x padding of 16
		if (usedSpace > w) {
			// base minwidth for overflow
			return 200;
		}

		return Math.max(200, w - usedSpace);
	}

	updateTaskCellMaxWidth() {
		const taskCellMaxWidth = this.getTaskCellMaxWidth();
		if (taskCellMaxWidth !== this.state.taskCellMaxWidth) {
			this.setState({taskCellMaxWidth: taskCellMaxWidth});
		}
	}

	onWindowResize() {
		this.updateTaskCellMaxWidth();
	}

	onExpansionClick(e) {
		e.stopPropagation();
		e.preventDefault();
		const newTarget = e.relatedTarget || e.explicitOriginalTarget || document.activeElement;

		if (
			(newTarget &&
				newTarget.className &&
				(newTarget.className.toLowerCase().includes('calendar') ||
					newTarget.className.toLowerCase().includes('-control'))) ||
			newTarget.tagName === 'INPUT' ||
			newTarget.className.toLowerCase().includes('clear-dates') ||
			newTarget.className.toLowerCase().includes('modal')
		) {
			return;
		}
		// Prevent expansion when warning is clicked
		if (e.target && e.target.className && e.target.className.includes('warning')) {
			return;
		}
		this.setState(prevState => {
			return {expanded: !prevState.expanded};
		});
	}

	deletePeriod() {
		const periodId = this.props.period.node.id;
		const rollovers = getRolloversFromPeriods(this.props.project.retainerPeriods, [periodId]);
		showModal({
			type: MODAL_TYPE.DELETE_RETAINER_PERIOD_MODAL,
			rollovers,
			periodIds: [periodId],
			projectId: this.props.project.id,
			periodBudgetType: this.props.period.node.periodBudgetType,
			currency: this.props.currency,
		});
	}

	// Locking a price-based period writes a rollover derived from period price fields that are only
	// readable with financial permission, so the action is withheld from a viewer who cannot read
	// them. Hours-based periods derive their rollover from ungated fields and stay available.
	canLockPeriod() {
		return this.props.inHours || !!this.props.hasFinancialAccess;
	}

	lockPeriod() {
		const {formatMessage} = this.props.intl;
		const period = this.props.period.node;
		// Safety net for the lock affordances that are already hidden when this is false: without it
		// the price branch below would coerce the gated null fields to 0 and persist a wrong amount.
		if (!this.canLockPeriod()) {
			return;
		}
		const periodEndDate = Util.CreateMomentDate(period.endYear, period.endMonth, period.endDay);
		const unlockedFuturePeriodsAmount = this.props.viewer.project.retainerPeriods.edges.filter(filterPeriod => {
			const filterPeriodStartDate = Util.CreateMomentDate(
				filterPeriod.node.startYear,
				filterPeriod.node.startMonth,
				filterPeriod.node.startDay
			);
			return !filterPeriod.node.periodLocked && filterPeriodStartDate.isAfter(periodEndDate);
		}).length;
		let periodRollover = null;

		const inHours = this.props.inHours;
		const periodDifference = inHours
			? period.financialNumbers.retainerPeriodRolloverMinutes / 60
			: period.financialNumbers.retainerPeriodRolloverPrice;
		if (inHours) {
			const totalTime = this.props.timeRegs.nonConflictTimeRegs.reduce(
				(acc, timeReg) => acc + timeReg.node.billableMinutesRegistered / 60,
				0
			);
			periodRollover = periodDifference + period.periodHoursAmount - totalTime;
		} else {
			const totalCost = this.props.timeRegs.nonConflictTimeRegs.reduce((acc, timeReg) => acc + timeReg.node.price, 0);
			// Part-of-fixed-price expenses associated with the period also consume the period
			// budget, so they must be subtracted from the rollover amount.
			const totalExpenses = getPeriodExpensesTotal(period);
			periodRollover = periodDifference + period.periodPriceAmount - totalCost - totalExpenses;
		}

		const selectionText = selection => {
			if (selection === 'roll-next') {
				return (
					(periodRollover < 0
						? formatMessage({id: 'retainer.subtract_next_value'})
						: formatMessage({id: 'retainer.roll_next_value'})) +
					': ' +
					this.props.withSuffix(Math.abs(periodRollover), {format: 'always_two_decimal'})
				);
			} else if (selection === 'roll-split') {
				return (
					(periodRollover < 0
						? formatMessage({id: 'retainer.subtract_split_value'})
						: formatMessage({id: 'retainer.roll_split_value'})) +
					': ' +
					this.props.withSuffix(Math.abs(periodRollover) / unlockedFuturePeriodsAmount, {
						format: 'always_two_decimal',
					})
				);
			}
		};

		const onSuccess = () => {
			dispatch(EVENT_ID.RETAINER_UPDATE_PROJECT);
			createToast({duration: 5000, message: 'Period Locked'});
			trackEvent('Retainer Period', 'Locked', {invoiced: period.invoiced});
		};
		const callbackPositive = params => {
			const mutationObject = {
				id: period.id,
				projectId: this.props.viewer.project.id,
				periodLocked: true,
			};
			if (period.periodBudgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS) {
				// The value of the number has no impact on the data when not splitting, but is required when splitting.
				mutationObject.periodDifferenceHoursAmount = periodRollover;
				if (params === 'roll-next' || params === 'roll-split') {
					// TODO: This means that any conflict decisions taken on this period will be overwritten,
					// but is required to allow rollover on a period which had previously been set to ignore
					mutationObject.ignoredRolloverHours = 0;
				} else if (params === 'ignore') {
					mutationObject.periodDifferenceHoursAmount = 0;
					mutationObject.ignoredRolloverHours = periodRollover;
				}
			} else if (period.periodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE) {
				mutationObject.periodDifferencePriceAmount = periodRollover;
				if (params === 'roll-next' || params === 'roll-split') {
					// TODO: This means that any conflict decisions taken on this period will be overwritten,
					// but is required to allow rollover on a period which had previously been set to ignore
					mutationObject.ignoredRolloverPrice = 0;
				} else if (params === 'ignore') {
					mutationObject.periodDifferenceHoursAmount = 0;
					mutationObject.ignoredRolloverPrice = periodRollover;
				}
			}
			if (params === 'roll-split') {
				mutationObject.splitRollover = true;
			}
			Util.CommitMutation(UpdateRetainerPeriodMutation, mutationObject, onSuccess);
		};
		if (
			period.periodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS ||
			unlockedFuturePeriodsAmount === 0 ||
			periodRollover === 0
		) {
			callbackPositive();
		} else {
			showModal({
				type: MODAL_TYPE.WARNING_RADIO_BUTTON_MODAL,
				warningInformation: [
					periodRollover < 0 ? (
						<FormattedHTMLMessage id={'retainer.warning.lock_period_subtract'} />
					) : (
						<FormattedHTMLMessage id={'retainer.warning.lock_period_roll'} />
					),
				],
				callbackPositive: callbackPositive.bind(this),
				selectedOptionKey:
					this.props.project.defaultPeriodSettingRollValue && periodRollover >= 0
						? 'roll-next'
						: this.props.project.defaultPeriodSettingSubtractValue && periodRollover < 0
						? 'roll-next'
						: 'ignore',
				radioOptions: [
					{
						text:
							periodRollover < 0
								? formatMessage({id: 'retainer.subtract_next'})
								: formatMessage({id: 'retainer.roll_next'}),
						key: 'roll-next',
					},
					{
						text:
							periodRollover < 0
								? formatMessage({id: 'retainer.subtract_split'})
								: formatMessage({id: 'retainer.roll_split'}),
						key: 'roll-split',
					},
					{
						text: formatMessage({id: 'retainer.ignore'}),
						key: 'ignore',
					},
				],
				selectionText: selectionText.bind(this),
			});
		}
	}

	unlockPeriod(allTimeRegs) {
		const periodId = this.props.period.node.id;
		const rollovers = getRevertableRolloversFromPeriods(this.props.project.retainerPeriods, [periodId]);

		const unlockPeriodMutations = rolloverIdsToRevert => {
			const revertRollovers = !!rolloverIdsToRevert;

			const onSuccess = response => {
				trackEvent('Retainer Period', 'Unlocked', {
					revertRollovers,
					periodBudgetType: this.props.project.defaultPeriodBudgetType,
				});

				dispatch(EVENT_ID.RETAINER_UPDATE_PROJECT);

				if (response.updateRetainerPeriod.errors?.length) {
					createToast({
						duration: 5000,
						message: this.props.intl.formatMessage({id: 'retainer.unlocked_invoiced_period_error'}),
						callback: () => {
							location.reload();
						},
					});
					return;
				}

				const onRetainerUpdateSuccess = () => {
					createToast({duration: 5000, message: 'Period Unlocked'});
					this.props.onPeriodsChanged();
				};

				if (allTimeRegs.length > 0) {
					Util.CommitMutation(
						UpdateTimeRegistrationRetainerConflict,
						{ids: allTimeRegs.map(t => t.node.id), retainerConflictHandled: false, overrideInvoiced: true},
						onRetainerUpdateSuccess
					);
				} else {
					onRetainerUpdateSuccess();
				}
			};

			const callbackPositive = () => {
				Util.CommitMutation(
					UpdateRetainerPeriodMutation,
					{
						id: this.props.period.node.id,
						periodLocked: false,
						periodLockedTime: null,
						rolloverIdsToRevert: rolloverIdsToRevert,
					},
					onSuccess
				);
			};
			if (this.props.timeRegs.conflictTimeRegs.length > 0) {
				showModal({
					type: MODAL_TYPE.WARNING,
					warningInformation: [
						'Unlocking a period will cause all conflicting time registrations to be added to the period. Do you still wish to unlock the period?',
					],
					buttons: [
						{
							text: this.props.intl.formatMessage({id: 'common.cancel'}),
							style: BUTTON_STYLE.FILLED,
							color: BUTTON_COLOR.WHITE,
						},
						{
							text: this.props.intl.formatMessage({id: 'retainer.unlock_period'}),
							callback: callbackPositive,
							style: BUTTON_STYLE.FILLED,
							color: BUTTON_COLOR.GREEN,
						},
					],
				});
			} else {
				callbackPositive();
			}
		};

		if (rollovers.length) {
			showModal({
				type: MODAL_TYPE.REVERT_ROLLOVER_MODAL,
				rollovers: rollovers,
				periodBudgetType: this.props.period.node.periodBudgetType,
				currency: this.props.currency,
				unlockPeriodMutations: unlockPeriodMutations,
				isBulkUnlock: false,
			});
		} else {
			unlockPeriodMutations(null);
		}
	}

	render() {
		const {period, timeRegs, project} = this.props;
		const allTimeRegs = timeRegs.conflictTimeRegs.concat(timeRegs.nonConflictTimeRegs);
		const isTimeMaterials = project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS;
		const inHours = this.props.inHours;
		const periodExpenses = period.node.expenses ? period.node.expenses.edges.map(edge => edge.node) : [];
		return (
			<div className={'retainer-period-container'}>
				<ProjectRetainerPeriodHeader
					userpilotIndex={this.props.userpilotIndex}
					withSuffix={this.props.withSuffix}
					inHours={inHours}
					expanded={this.state.expanded}
					onExpansionClick={this.onExpansionClick.bind(this)}
					backgroundColor={project.projectColor}
					timeRegs={timeRegs.nonConflictTimeRegs}
					project={this.props.viewer.project}
					currency={this.props.currency}
					viewer={this.props.viewer}
					period={period}
					remainingPlanForPeriod={this.props.remainingPlanForPeriod}
					deletePeriod={this.deletePeriod.bind(this)}
					resetSelectedPeriods={this.props.resetSelectedPeriods}
					lockPeriod={this.lockPeriod.bind(this)}
					canLockPeriod={this.canLockPeriod()}
					hasFinancialAccess={this.props.hasFinancialAccess}
					unlockPeriod={this.unlockPeriod.bind(this, allTimeRegs)}
					conflictedTimeEntries={timeRegs.conflictTimeRegs}
					projectLocked={this.props.projectLocked}
					useTimeApproval={this.props.useTimeApproval}
					handleSelectedChanged={this.props.handleSelectedChanged}
					isSelected={this.props.isSelected}
					programBudgetInfo={this.props.programBudgetInfo}
					hasTarget={this.props.hasTarget}
					enabledColumns={this.props.enabledColumns}
				/>
				{this.state.expanded ? (
					<div className={'retainer-period-contents'}>
						<ProjectRetainerPeriodDataVisuals
							currency={this.props.currency}
							withSuffix={this.props.withSuffix}
							inHours={inHours}
							isTimeMaterials={isTimeMaterials}
							isFixedPrice={this.props.isFixedPrice}
							conflictedTimeEntries={timeRegs.conflictTimeRegs}
							projectColor={project.projectColor}
							timeRegs={timeRegs.nonConflictTimeRegs}
							lockPeriod={this.lockPeriod.bind(this)}
							canLockPeriod={this.canLockPeriod()}
							hasFinancialAccess={this.props.hasFinancialAccess}
							period={period}
							viewer={this.props.viewer}
							useTimeApproval={this.props.useTimeApproval}
						/>
						<ProjectRetainerPeriodTable
							cy={'period-table'}
							conflictedTimeEntries={timeRegs.conflictTimeRegs}
							taskCellMaxWidth={this.state.taskCellMaxWidth}
							availableColumns={this.state.availableColumns}
							showTaskModal={this.props.showTaskModal}
							hasFinancialAccess={this.props.hasFinancialAccess}
							viewer={this.props.viewer}
							project={project}
							timeRegs={allTimeRegs}
							projectLocked={isProjectLocked(project)}
							period={period}
							isHarvestProject={this.props.isHarvestProject}
						/>
						{hasFeatureFlag('expenses_on_fixed_price_retainer_periods') && periodExpenses.length > 0 && (
							<ProjectRetainerPeriodExpensesTable
								expenses={periodExpenses}
								currencySymbol={Util.GetCurrencySymbol(this.props.currency)}
							/>
						)}
					</div>
				) : null}
			</div>
		);
	}
}

ProjectRetainerPeriod.propTypes = {
	period: PropTypes.object.isRequired,
	project: PropTypes.object.isRequired,
	timeRegs: PropTypes.object.isRequired,
};
export default injectIntl(ProjectRetainerPeriod);
